import type { DecoratedNoteWithAttachments } from '@witmetrics/utils';
import { FILE_TYPE_ID } from '@witmetrics/utils/constants';
import { isEmpty } from '@witmetrics/utils/misc';
import { isBlank } from '@witmetrics/utils/strings';
import { asyncMap } from '@witmetrics/utils/arrays';
import { downloadFileVersion, downloadPreview } from '@/utils/files';
import { theme } from '@/utils/theme';
import type { LoadedAttachment } from './types';

export const classes = {
  card: 'flex flex-col my-2 px-0 py-0 rounded-xl shrink-0 transition-colors border border-grey-3',
  auroraCard:
    'rounded-[14px] border-black/10 bg-white/90 shadow-aurora-card overflow-hidden dark:border-white/10 dark:bg-[#332e43]/[0.84]',
  highlighted: 'border-2 border-orange-2',
  contentWrapper: 'content-wrapper px-[13px] py-3 flex flex-col',
  notesWrapper: 'mt-[7px] whitespace-pre-wrap',
  noteBody: 'text-[13px] text-taupe-2 dark:text-mauve-2',
  notesField: 'shadow-none border border-grey-4',
  linkWrapper: 'flex items-center px-2 pb-2',
  linkName:
    'ml-2 text-sm text-grey-7 hover:underline cursor-pointer outline-none',
  caseType: 'size-6 rounded-md p-0',
  caseTypeIcon: 'size-5',
};

export const styles = {
  card: {
    '& .options-wrapper': {
      opacity: '0',
      transition: 'opacity 0.1s',
    },
    '&:hover .content-wrapper .options-wrapper': {
      opacity: '1',
    },
  },
  auroraDark: {
    '.dark &': {
      '& .text-grey-8': { color: theme.palette['mauve-1'].main },
      '& .text-grey-7': { color: theme.palette['mauve-2'].main },
      '& .text-grey-6': { color: theme.palette['mauve-3'].main },
      '& .bg-grey-2': { backgroundColor: 'rgba(255,255,255,0.04)' },
      '& .bg-grey-3': { backgroundColor: 'rgba(255,255,255,0.1)' },
    },
  },
};

export function isAuthor(
  note?: DecoratedNoteWithAttachments,
  currentUserId?: number
) {
  if (!note || !currentUserId) return false;
  return note.createdBy?.id === currentUserId;
}

export function isHighlighted(
  note: DecoratedNoteWithAttachments | undefined,
  searchParams: URLSearchParams
) {
  if (!note || !searchParams.get('highlightedNoteId')) return false;
  return note.id === parseInt(searchParams.get('highlightedNoteId')!);
}

export function getAttachments(note?: DecoratedNoteWithAttachments) {
  // Filter out files that aren't attachments or don't have a preview source
  if (!note) return [];
  return note.noteFiles.filter(
    ({ file }) =>
      file.fileTypeId === FILE_TYPE_ID.ATTACHMENT &&
      !isBlank(file.versions[0]?.previewSource)
  );
}

export async function loadAttachments(
  note?: DecoratedNoteWithAttachments
): Promise<LoadedAttachment[]> {
  const attachments = getAttachments(note);
  if (isEmpty(attachments)) return [];
  return asyncMap(attachments, async (noteFile) => {
    const fileVersionId = noteFile.file.versions[0].id;
    const [sourceBuffer, previewBuffer] = await Promise.all([
      downloadFileVersion(fileVersionId),
      downloadPreview(fileVersionId),
    ]);
    return {
      noteFile,
      source: URL.createObjectURL(new Blob([sourceBuffer])),
      previewSource: URL.createObjectURL(new Blob([previewBuffer])),
    };
  });
}
